import { memo, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

/**
 * @typedef {'xs' | 'sm' | 'md' | 'xl'} NumericFieldSize
 */

/**
 * Campo numérico con densidades **xs / sm / md / xl** (`size`; **xl** = `min-h-[44px]`). Stepper pill accent/neutral;
 * hover/focus opcional en el stepper (`stepperOnHover`).
 *
 * Use `onValueChange` for the parsed number; empty input yields `null`.
 *
 * @param {object} props
 * @param {NumericFieldSize} [props.size='xs']
 * @param {string} [props.label]
 * @param {string} [props.error]
 * @param {string} [props.helperText]
 * @param {import('lucide-react').LucideIcon} [props.leftIcon]
 * @param {import('lucide-react').LucideIcon} [props.rightIcon]
 * @param {string} [props.id]
 * @param {string} [props.className]
 * @param {string} [props.inputClassName]
 * @param {number|null|undefined} props.value
 * @param {(next: number | null) => void} [props.onValueChange]
 * @param {number} [props.min]
 * @param {number} [props.max]
 * @param {number|string} [props.step]
 * @param {string} [props.placeholder]
 * @param {boolean} [props.disabled]
 * @param {boolean} [props.showStepper]
 * @param {boolean} [props.stepperOnHover]
 */
const SHELL_BASE =
  'group flex w-full items-center overflow-hidden border bg-surface-muted transition-colors';

const INPUT_CORE =
  'min-h-0 min-w-0 flex-1 border-0 bg-transparent text-content outline-none placeholder:text-content-faint [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none';

const STEPPER_ICON_BASE = 'shrink-0 text-neutral-800 dark:text-white';

const STEPPER_BOX_BASE =
  'flex shrink-0 flex-col overflow-hidden rounded-sm border border-neutral-200/90 bg-white shadow-sm dark:border-edge dark:bg-surface-elevated';

const STEPPER_GUTTER_BASE =
  'flex shrink-0 items-center justify-end self-center overflow-hidden transition-[width,max-width,opacity] duration-150 ease-out';

const STEPPER_GUTTER_HIDDEN = 'w-0 max-w-0 opacity-0 pointer-events-none';

/** @type {Record<NumericFieldSize, { shell: string; outerGap: string; label: string; input: string; iconPad: string; iconWrap: string; iconRight: string; lucideSide: number; stepperMy: string; stepperW: string; stepperBtn: string; lucideChevron: number; chevronAbsStroke: boolean; chevronStroke: number; gutterHover: string; gutterFocus: string; gutterPinned: string; gutterAlways: string; helper: string; error: string }>} */
const SIZE_PRESETS = {
  xs: {
    shell: 'min-h-8 rounded-md',
    outerGap: 'gap-1',
    label: 'text-[11px] font-medium leading-none text-content-secondary',
    input: 'py-1.5 pl-2 pr-2 text-xs leading-tight',
    iconPad: 'pl-6',
    iconWrap: 'left-1.5',
    iconRight: 'right-1.5',
    lucideSide: 12,
    stepperMy: 'my-0.5',
    stepperW: 'w-4',
    stepperBtn: 'h-2',
    lucideChevron: 7,
    chevronAbsStroke: false,
    chevronStroke: 2,
    gutterHover:
      'group-hover:w-7 group-hover:max-w-[1.75rem] group-hover:pr-1 group-hover:opacity-100 group-hover:pointer-events-auto',
    gutterFocus:
      'group-focus-within:w-7 group-focus-within:max-w-[1.75rem] group-focus-within:pr-1 group-focus-within:opacity-100 group-focus-within:pointer-events-auto',
    gutterPinned: 'w-7 max-w-[1.75rem] pr-1 opacity-100 pointer-events-auto',
    gutterAlways: 'flex w-7 max-w-[1.75rem] shrink-0 items-center justify-end self-center pr-1 opacity-100',
    helper: 'text-[11px] text-content-muted',
    error: 'text-[11px] text-red-500 dark:text-red-400',
  },
  sm: {
    shell: 'min-h-9 rounded-md',
    outerGap: 'gap-1.5',
    label: 'text-xs font-medium text-content-secondary',
    input: 'py-2 pl-2 pr-2 text-xs leading-snug',
    iconPad: 'pl-7',
    iconWrap: 'left-2',
    iconRight: 'right-2',
    lucideSide: 14,
    stepperMy: 'my-0.5',
    stepperW: 'w-[1.125rem]',
    stepperBtn: 'h-2.5',
    lucideChevron: 8,
    chevronAbsStroke: true,
    chevronStroke: 2.25,
    gutterHover:
      'group-hover:w-8 group-hover:max-w-[2rem] group-hover:pr-1.5 group-hover:opacity-100 group-hover:pointer-events-auto',
    gutterFocus:
      'group-focus-within:w-8 group-focus-within:max-w-[2rem] group-focus-within:pr-1.5 group-focus-within:opacity-100 group-focus-within:pointer-events-auto',
    gutterPinned: 'w-8 max-w-[2rem] pr-1.5 opacity-100 pointer-events-auto',
    gutterAlways: 'flex w-8 max-w-[2rem] shrink-0 items-center justify-end self-center pr-1.5 opacity-100',
    helper: 'text-xs text-content-muted',
    error: 'text-xs text-red-500 dark:text-red-400',
  },
  md: {
    shell: 'min-h-10 rounded-md',
    outerGap: 'gap-1.5',
    label: 'text-xs font-medium text-content-secondary',
    input: 'py-2 pl-2.5 pr-2.5 text-sm leading-snug',
    iconPad: 'pl-8',
    iconWrap: 'left-2.5',
    iconRight: 'right-2.5',
    lucideSide: 16,
    stepperMy: 'my-1',
    stepperW: 'w-5',
    stepperBtn: 'h-3',
    lucideChevron: 8,
    chevronAbsStroke: true,
    chevronStroke: 2.25,
    gutterHover:
      'group-hover:w-9 group-hover:max-w-[2.25rem] group-hover:pr-2 group-hover:opacity-100 group-hover:pointer-events-auto',
    gutterFocus:
      'group-focus-within:w-9 group-focus-within:max-w-[2.25rem] group-focus-within:pr-2 group-focus-within:opacity-100 group-focus-within:pointer-events-auto',
    gutterPinned: 'w-9 max-w-[2.25rem] pr-2 opacity-100 pointer-events-auto',
    gutterAlways: 'flex w-9 max-w-[2.25rem] shrink-0 items-center justify-end self-center pr-2 opacity-100',
    helper: 'text-xs text-content-muted',
    error: 'text-xs text-red-500 dark:text-red-400',
  },
  xl: {
    shell: 'min-h-[44px] rounded-lg',
    outerGap: 'gap-2',
    label: 'text-sm font-medium text-content-secondary',
    input: 'py-2.5 pl-3 pr-3 text-sm leading-normal',
    iconPad: 'pl-10',
    iconWrap: 'left-3',
    iconRight: 'right-3',
    lucideSide: 18,
    stepperMy: 'my-1',
    stepperW: 'w-6',
    stepperBtn: 'h-3.5',
    lucideChevron: 9,
    chevronAbsStroke: true,
    chevronStroke: 2.5,
    gutterHover:
      'group-hover:w-10 group-hover:max-w-[2.5rem] group-hover:pr-2 group-hover:opacity-100 group-hover:pointer-events-auto',
    gutterFocus:
      'group-focus-within:w-10 group-focus-within:max-w-[2.5rem] group-focus-within:pr-2 group-focus-within:opacity-100 group-focus-within:pointer-events-auto',
    gutterPinned: 'w-10 max-w-[2.5rem] pr-2 opacity-100 pointer-events-auto',
    gutterAlways: 'flex w-10 max-w-[2.5rem] shrink-0 items-center justify-end self-center pr-2 opacity-100',
    helper: 'text-xs text-content-muted',
    error: 'text-xs text-red-500 dark:text-red-400',
  },
};

/** @param {number} n @param {number|undefined} min @param {number|undefined} max */
function clampNum(n, min, max) {
  if (!Number.isFinite(n)) return null;
  let x = n;
  if (typeof min === 'number' && x < min) x = min;
  if (typeof max === 'number' && x > max) x = max;
  return x;
}

const NumericField = ({
  label,
  error,
  helperText,
  leftIcon: LeftIcon,
  rightIcon: RightIcon,
  id,
  className = '',
  inputClassName = '',
  value,
  onValueChange,
  min,
  max,
  step = 'any',
  placeholder = '',
  disabled = false,
  showStepper = true,
  stepperOnHover = true,
  size = 'xs',
  ...rest
}) => {
  const sz = SIZE_PRESETS[size] ?? SIZE_PRESETS.xs;

  const inputId = id || (label ? label.toLowerCase().replace(/\s+/g, '-') : undefined);
  const [stepperHovered, setStepperHovered] = useState(false);

  const display =
    value === null || value === undefined || Number.isNaN(Number(value)) ? '' : String(value);

  const stepNum =
    step === 'any' || step === undefined || step === '' ? 1 : Number(step);
  const safeStep = Number.isFinite(stepNum) && stepNum > 0 ? stepNum : 1;

  const numericValue =
    value === null || value === undefined || Number.isNaN(Number(value)) ? null : Number(value);

  const atMin = typeof min === 'number' && numericValue !== null && numericValue <= min;
  const atMax = typeof max === 'number' && numericValue !== null && numericValue >= max;

  const shellClass = [
    SHELL_BASE,
    sz.shell,
    error
      ? 'border-red-400 focus-within:border-red-500 dark:border-red-500'
      : 'border-edge hover:border-accent/50 focus-within:border-accent/50',
    disabled ? 'pointer-events-none opacity-60' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const inputClassNameResolved = [
    INPUT_CORE,
    sz.input,
    LeftIcon ? sz.iconPad : '',
    inputClassName,
  ]
    .filter(Boolean)
    .join(' ');

  const stepperBoxClass = [STEPPER_BOX_BASE, sz.stepperMy, sz.stepperW].filter(Boolean).join(' ');

  const stepperBtnClass = [
    'flex w-full shrink-0 items-center justify-center transition-colors hover:bg-neutral-100/90 dark:hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-40',
    sz.stepperBtn,
  ].join(' ');

  const stepperGutterClass = useMemo(() => {
    if (!showStepper) return '';
    if (stepperOnHover) {
      return [
        STEPPER_GUTTER_BASE,
        STEPPER_GUTTER_HIDDEN,
        sz.gutterHover,
        sz.gutterFocus,
        stepperHovered ? sz.gutterPinned : '',
      ]
        .filter(Boolean)
        .join(' ');
    }
    return sz.gutterAlways;
  }, [showStepper, stepperOnHover, stepperHovered, sz]);

  const handleChange = (e) => {
    const raw = e.target.value;
    if (raw === '' || raw === '-') {
      onValueChange?.(null);
      return;
    }
    const n = Number(raw);
    if (!Number.isFinite(n)) return;
    onValueChange?.(n);
  };

  const bump = (delta) => {
    if (disabled) return;
    const base =
      numericValue !== null ? numericValue : typeof min === 'number' ? min : 0;
    const next = clampNum(base + delta * safeStep, min, max);
    if (next !== null) onValueChange?.(next);
  };

  const outerClass = ['flex', 'flex-col', sz.outerGap, className].filter(Boolean).join(' ');

  return (
    <div className={outerClass}>
      {label ? (
        <label htmlFor={inputId} className={sz.label}>
          {label}
        </label>
      ) : null}

      <div className={shellClass}>
        <div className="relative min-w-0 flex-1">
          {LeftIcon ? (
            <span
              className={`pointer-events-none absolute top-1/2 -translate-y-1/2 text-content-muted ${sz.iconWrap}`}
            >
              <LeftIcon size={sz.lucideSide} />
            </span>
          ) : null}

          <input
            id={inputId}
            type="number"
            inputMode="decimal"
            value={display}
            onChange={handleChange}
            min={min}
            max={max}
            step={step}
            disabled={disabled}
            placeholder={placeholder}
            className={inputClassNameResolved}
            {...rest}
          />

          {RightIcon && !showStepper ? (
            <span
              className={`pointer-events-none absolute top-1/2 -translate-y-1/2 text-content-muted ${sz.iconRight}`}
            >
              <RightIcon size={sz.lucideSide} />
            </span>
          ) : null}

          {RightIcon && showStepper ? (
            <span className="pointer-events-none flex shrink-0 items-center pr-0.5 text-content-muted">
              <RightIcon size={sz.lucideSide} />
            </span>
          ) : null}
        </div>

        {showStepper ? (
          <div
            className={stepperGutterClass}
            onMouseEnter={() => {
              if (stepperOnHover) setStepperHovered(true);
            }}
            onMouseLeave={() => {
              setStepperHovered(false);
            }}
          >
            <div className={stepperBoxClass}>
              <button
                type="button"
                tabIndex={-1}
                disabled={disabled || atMax}
                className={stepperBtnClass}
                aria-label="Increment"
                onClick={() => bump(1)}
              >
                <ChevronUp
                  className={STEPPER_ICON_BASE}
                  size={sz.lucideChevron}
                  strokeWidth={sz.chevronStroke}
                  {...(sz.chevronAbsStroke ? { absoluteStrokeWidth: true } : {})}
                  aria-hidden
                />
              </button>
              <div className="mx-0.5 h-px shrink-0 bg-neutral-200/90 dark:bg-white/15" aria-hidden />
              <button
                type="button"
                tabIndex={-1}
                disabled={disabled || atMin}
                className={stepperBtnClass}
                aria-label="Decrement"
                onClick={() => bump(-1)}
              >
                <ChevronDown
                  className={STEPPER_ICON_BASE}
                  size={sz.lucideChevron}
                  strokeWidth={sz.chevronStroke}
                  {...(sz.chevronAbsStroke ? { absoluteStrokeWidth: true } : {})}
                  aria-hidden
                />
              </button>
            </div>
          </div>
        ) : null}
      </div>

      {error ? <p className={sz.error}>{error}</p> : null}
      {helperText && !error ? <p className={sz.helper}>{helperText}</p> : null}
    </div>
  );
};

export default memo(NumericField);
